/*
 * addonIntroFlow — shared sequence engine for the two "Sophia is live"
 * add-on intro screens (ScreenCallsIntro = "contact", CalendarBookingIntroooo
 * = "calendar") reached from the dialog on /go-live-setup.
 *
 * Both screens are shown as an ordered SEQUENCE. Forward exits (primary
 * action AND Skip) advance to the next screen; the dashboard comes only
 * after the last screen.
 *
 * POSITION model (not a consumable queue): we store the FULL ordered
 * sequence and derive "next" from the CURRENT screen's key. This is what
 * lets browser/device Back walk the sequence backwards (calendar →
 * contact) and then forwards again without the state going stale — the
 * sequence is never mutated as the user moves through it, only the
 * current screen's identity matters.
 *
 * State lives in sessionStorage (NOT a query param) so it survives the
 * navigations that drop URL state:
 *   - contact "Allow" → /contact-sync (full page) → "Complete Setup"
 *   - calendar "Connect" → Google OAuth full-page redirect → back to
 *     /go-live-setup, where GoLiveWizard resumes the sequence.
 *
 * agentId / userId are stashed so downstream routes keep context even
 * after the OAuth redirect strips the original query string.
 */

export const ADDON_ROUTE_BY_KEY = {
  contact: "/screen-your-calls",
  calendar: "/calendar-access",
};

// Where the flow lands once every screen has been shown.
export const ADDON_FINISH_ROUTE = "/fast-agent-detail";

// Read by FastAgentDetails to pop the one-shot "Setup Summary" dialog +
// confetti. Mirror of ContactSyncStep's SETUP_SUMMARY_FLAG so finishing
// the add-on flow (without passing through ContactSyncStep) still shows
// the summary on the dashboard.
const SETUP_SUMMARY_FLAG = "fastAgentDetail.showSetupSummary";

const SEQ_KEY = "addonIntroFlow.sequence"; // JSON array, full ordered keys
const SEARCH_KEY = "addonIntroFlow.search"; // preserved agentId/userId qs
const OAUTH_RETURN_KEY = "addonIntroFlow.oauthReturn"; // calendar OAuth in flight
/*
 * Where THIS run of the flow should land when the last screen is done.
 *
 * The finish route used to be the ADDON_FINISH_ROUTE constant, so every
 * completion went to /fast-agent-detail — correct for first-time onboarding,
 * wrong for a user who entered the sequence from /setup-ready and expects to
 * return there.
 *
 * Captured at startAddonFlow() and cleared by clearAddonFlow(), so it lives
 * exactly as long as the flow does. Deliberately NOT read from a marker at
 * finish time: markers set on an earlier, unrelated visit could still be
 * around and would send the wrong run to the wrong place.
 */
const FINISH_KEY = "addonIntroFlow.finish";

// Only these may be stored as a finish route — the value reaches navigate()
// directly, so an arbitrary string would be an open redirect.
const ALLOWED_FINISH_ROUTES = ["/fast-agent-detail", "/setup-ready"];

const readFinishRoute = () => {
  try {
    const v = sessionStorage.getItem(FINISH_KEY);
    return ALLOWED_FINISH_ROUTES.includes(v) ? v : null;
  } catch {
    return null;
  }
};

const readSequence = () => {
  try {
    const raw = sessionStorage.getItem(SEQ_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeSequence = (arr) => {
  try {
    if (arr && arr.length) {
      sessionStorage.setItem(SEQ_KEY, JSON.stringify(arr));
    } else {
      sessionStorage.removeItem(SEQ_KEY);
    }
  } catch {
    /* private mode — flow just won't persist, harmless */
  }
};

const readBaseSearch = () => {
  try {
    return sessionStorage.getItem(SEARCH_KEY) || "";
  } catch {
    return "";
  }
};

const writeBaseSearch = (qs) => {
  try {
    if (qs) sessionStorage.setItem(SEARCH_KEY, qs);
    else sessionStorage.removeItem(SEARCH_KEY);
  } catch {
    /* harmless */
  }
};

// Keep only the stable context params (agentId / userId) from a search
// string so we don't thread transient params (googleAuth, …).
const cleanSearch = (search) => {
  const inp = new URLSearchParams(search || "");
  const out = new URLSearchParams();
  for (const key of ["agentId", "userId"]) {
    const val = inp.get(key);
    if (val) out.set(key, val);
  }
  return out.toString();
};

// "path?agentId=…&userId=…" for a screen key, using the stashed params.
export const pathForKey = (key) => {
  const path = ADDON_ROUTE_BY_KEY[key];
  if (!path) return ADDON_FINISH_ROUTE;
  const qs = readBaseSearch();
  return qs ? `${path}?${qs}` : path;
};

const setSummaryFlag = () => {
  try {
    sessionStorage.setItem(SETUP_SUMMARY_FLAG, "true");
  } catch {
    /* harmless — summary just won't show */
  }
};

/* Finish directly from the add-ons dialog ("Do it Later"). */
export const finishAddonFlow = () => {
  clearAddonFlow();
  setSummaryFlag();
  return ADDON_FINISH_ROUTE;
};

/*
 * Start a flow from a dialog button. `sequence` is the full ordered list
 * of screen keys; the first is the screen we navigate to now, the rest
 * stay stored for later derivation. `baseSearch` is the dialog host's
 * location.search (agentId / userId). Returns the path for the FIRST
 * screen.
 */
export const startAddonFlow = (sequence, baseSearch, finishRoute) => {
  const seq = Array.isArray(sequence) ? sequence.filter(Boolean) : [];
  if (!seq.length) return ADDON_FINISH_ROUTE;
  writeBaseSearch(cleanSearch(baseSearch));
  writeSequence(seq);
  try {
    // Remember where THIS run finishes. Whitelisted; anything else is
    // ignored and the flow falls back to the dashboard.
    if (ALLOWED_FINISH_ROUTES.includes(finishRoute)) {
      sessionStorage.setItem(FINISH_KEY, finishRoute);
    } else {
      sessionStorage.removeItem(FINISH_KEY);
    }
  } catch {
    /* harmless — falls back to ADDON_FINISH_ROUTE */
  }
  try {
    // Flag the "Sophia is live" dialog to reopen if the user Backs out
    // of the FIRST add-on screen to /go-live-setup (the wizard resumes
    // at the triage step and TriageStep re-pops the dialog).
    sessionStorage.setItem("goLive.reopenReadyDialog", "1");
  } catch {
    /* harmless */
  }
  return pathForKey(seq[0]);
};

/*
 * Start the add-on sequence for THIS run and return the path of its first
 * screen. The caller navigates; everything else about the run is decided here.
 *
 * Two callers reach this point:
 *
 *   - TriageStep — the user left the "Sophia is live" dialog (or the dialog was
 *     silently suppressed on a repeat pass).
 *
 *   - GoLiveWizard — a /setup-ready visit ran out of pending WIZARD steps. The
 *     ladder does not end at Triage: Triage is what launches the add-ons. So
 *     hopping over an already-configured Triage has to hand off here, otherwise
 *     contact sync and calendar are skipped along with it — which is exactly
 *     what happened when the wizard exited straight to /setup-ready.
 *
 * Shared deliberately: the "where does this run finish?" rule used to live
 * inside TriageStep, and a second copy in the wizard would have been a second
 * definition of the same decision — the failure shape behind several past bugs.
 */
export const beginAddonSequence = (sequence, baseSearch) => {
  try {
    /*
     * Onboarding is effectively done (the wizard reached its end). Callers keep
     * `goLiveWizard.lastStep` on purpose so Back from the first add-on screen
     * can resume the wizard at Triage; clearAddonFlow wipes it once the flow
     * finishes.
     */
    localStorage.setItem("onboardComplete", "true");
  } catch {
    /* private mode — harmless */
  }

  /*
   * Where this run of the sequence should FINISH.
   *
   * First-time onboarding ends on the dashboard with the Setup Summary. A user
   * replaying features from /setup-ready is not finishing onboarding — they
   * expect to land back on /setup-ready, whatever the last screen happens to be.
   *
   * `setupReady.suppressSetupSummary` is written by SetUpReady.handleStepAction
   * on every card click, so its presence here means this wizard run was entered
   * from Setup Ready. Read ONCE, now, and handed to startAddonFlow which stores
   * it for this run only — the finish decision must not depend on a marker still
   * being around later.
   */
  let finishRoute;
  try {
    if (sessionStorage.getItem("setupReady.suppressSetupSummary") === "true") {
      finishRoute = "/setup-ready";
    }
  } catch {
    /* storage unavailable — falls back to the dashboard */
  }

  return startAddonFlow(sequence, baseSearch, finishRoute);
};

// True while a sequence is stored (used by ContactSyncStep to decide
// whether to continue the sequence or finish to dashboard).
export const isAddonFlowActive = () => readSequence().length > 0;

/*
 * Forward from the CURRENT screen key to the next screen in the stored
 * sequence. When there is no next screen the flow is done: clear it, arm
 * the dashboard Setup Summary dialog, and return the finish route.
 *
 * Non-mutating w.r.t. the sequence — only computes the next position —
 * so Back can re-land on an earlier screen and advance again correctly.
 */
export const advanceFromKey = (currentKey) => {
  const seq = readSequence();
  const idx = seq.indexOf(currentKey);
  const next = idx >= 0 ? seq[idx + 1] : undefined;
  if (next) return pathForKey(next);

  /*
   * Last screen (or unknown key) → finish.
   *
   * Read the finish route BEFORE clearAddonFlow() wipes it.
   *
   * The Setup Summary is armed ONLY when finishing to the dashboard. It is a
   * first-time onboarding celebration; a user replaying the sequence from
   * /setup-ready is not finishing onboarding, so arming it there would fire a
   * "you're live!" popup on a screen it was never designed for.
   */
  const finish = readFinishRoute() || ADDON_FINISH_ROUTE;
  clearAddonFlow();
  if (finish === ADDON_FINISH_ROUTE) setSummaryFlag();
  return finish;
};

/*
 * Backward from the CURRENT screen key to the PREVIOUS screen in the
 * stored sequence. Returns its path, or null when the current screen is
 * the first (nothing behind it in the sequence). Used by the intro
 * screens' back guard so device Back walks the sequence in reverse.
 */
export const backFromKey = (currentKey) => {
  const seq = readSequence();
  const idx = seq.indexOf(currentKey);
  const prev = idx > 0 ? seq[idx - 1] : undefined;
  return prev ? pathForKey(prev) : null;
};

/*
 * Where Back goes from the FIRST add-on screen (nothing behind it in the
 * sequence): the "Sophia is live" dialog (add-ons popup) on
 * /go-live-setup. The dialog reopens via the goLive.reopenReadyDialog
 * flag set in startAddonFlow, and the wizard resumes at the triage step.
 */
export const addonDialogReturnPath = () => {
  const qs = readBaseSearch();
  return qs ? `/go-live-setup?${qs}` : "/go-live-setup";
};

export const clearAddonFlow = () => {
  writeSequence([]);
  writeBaseSearch("");
  try {
    sessionStorage.removeItem(OAUTH_RETURN_KEY);
    // Scoped to this run — a stale value would misroute the NEXT flow.
    sessionStorage.removeItem(FINISH_KEY);
    // Flow finished → don't leave the dialog-reopen flag or the wizard
    // resume step lingering (would spuriously re-pop the dialog if the
    // user ever returns to /go-live-setup).
    sessionStorage.removeItem("goLive.reopenReadyDialog");
    localStorage.removeItem("goLiveWizard.lastStep");
  } catch {
    /* harmless */
  }
};

// ── Calendar OAuth round-trip markers ──────────────────────────────
// Set right before the calendar OAuth redirect so GoLiveWizard knows,
// on the /go-live-setup return, to resume the sequence instead of
// rendering the wizard.
export const markAddonOAuthReturn = () => {
  try {
    sessionStorage.setItem(OAUTH_RETURN_KEY, "1");
  } catch {
    /* harmless */
  }
};

// Non-consuming peek — GoLiveWizard captures this once at first render.
export const isAddonOAuthReturnPending = () => {
  try {
    return sessionStorage.getItem(OAUTH_RETURN_KEY) === "1";
  } catch {
    return false;
  }
};

export const consumeAddonOAuthReturn = () => {
  try {
    sessionStorage.removeItem(OAUTH_RETURN_KEY);
  } catch {
    /* harmless */
  }
};
